import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";

// Styled components for the tree structure
const NodePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5), // Reduced from 2
  textAlign: "center",
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  position: "relative",
  backgroundColor: "#ffffff",
  border: "1px solid #e0e0e0",
  background: "linear-gradient(145deg, #ffffff, #f5f7ff)",
  borderColor: theme.palette.primary.light,
}));

const VerticalLine = styled(Box)(({ theme }) => ({
  width: 2,
  backgroundColor: theme.palette.primary.main,
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
}));

const HorizontalLine = styled(Box)(({ theme }) => ({
  height: 2,
  backgroundColor: theme.palette.primary.main,
  position: "absolute",
}));

// Arrow component
const Arrow = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: 0,
  height: 0,
  borderStyle: "solid",
  borderWidth: "5px 4px 0 4px", // Reduced from 6px 5px
  borderColor: `${theme.palette.primary.main} transparent transparent transparent`,
  left: "50%",
  transform: "translateX(-50%)",
}));

const SimpleFAIRModelVisualization = ({ data }) => {
  // Extract values from the data object
  const { mother, child1, child2 } = data;

  // Format currency for display
  const formatCurrency = (value) => {
    return `$${value}`;
  };

  return (
    <Box
      sx={{
        position: "relative",
        maxWidth: 600,
        mx: "auto",
        p: 2,
        height: "auto",
      }}
    >
      {/* Root node (ALE) */}
      <NodePaper
        sx={{
          width: "70%",
          maxWidth: 250, // Reduced from 300
          mx: "auto",
          mb: 6, // Reduced from 8
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          color="primary.main"
          sx={{ fontSize: "0.875rem" }}
        >
          {" "}
          {/* Reduced size */}
          Projected Annual Loss Expectancy
        </Typography>
        <Typography
          variant="h5"
          fontWeight="bold"
          color="primary.dark"
          sx={{ fontSize: "1.25rem" }}
        >
          {" "}
          {/* Reduced from h4 */}
          {formatCurrency(mother)}
        </Typography>
      </NodePaper>

      {/* Vertical connecting line from ALE */}
      <VerticalLine
        sx={{
          top: 90, // Adjusted from 140
          height: 40, // Reduced from 50
        }}
      />

      {/* Down arrow from ALE */}
      <Arrow
        sx={{
          top: 125, // Adjusted from 185
        }}
      />

      {/* Horizontal line connecting children */}
      <HorizontalLine
        sx={{
          top: 130, // Adjusted from 190
          left: "20%",
          width: "60%",
        }}
      />

      {/* Vertical lines to children */}
      <VerticalLine
        sx={{
          top: 130, // Adjusted from 190
          height: 30, // Reduced from 40
          left: "20%",
        }}
      />

      <VerticalLine
        sx={{
          top: 130, // Adjusted from 190
          height: 30, // Reduced from 40
          left: "80%",
        }}
      />

      {/* Down arrows to children */}
      <Arrow
        sx={{
          top: 160, // Adjusted from 230
          left: "20%",
        }}
      />

      <Arrow
        sx={{
          top: 160, // Adjusted from 230
          left: "80%",
        }}
      />

      {/* Child nodes container */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          position: "relative",
          mt: 10, // Reduced from 12
          px: { xs: 1, sm: 2 },
          width: "100%",
        }}
      >
        {/* TLM Node (Left) */}
        <NodePaper
          sx={{
            width: "40%",
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="primary.main"
            sx={{ fontSize: "0.875rem" }}
          >
            {" "}
            {/* Reduced size */}
            Mean Loss Event Frequency
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            color="primary.dark"
            sx={{ fontSize: "1.25rem" }}
          >
            {" "}
            {/* Reduced from h4 */}
            {child2.toFixed(2)}
          </Typography>
        </NodePaper>

        {/* TEF × Probability Node (Right) */}
        <NodePaper
          sx={{
            width: "40%",
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="primary.main"
            sx={{ fontSize: "0.875rem" }}
          >
            {" "}
            {/* Reduced size */}
            Mean Loss Magnitude
          </Typography>
          <Typography
            variant="h6"
            fontWeight="bold"
            color="primary.dark"
            sx={{ fontSize: "1rem" }}
          >
            {" "}
            {/* Reduced size */}
            {formatCurrency(child1)}
          </Typography>
        </NodePaper>
      </Box>
    </Box>
  );
};

export default SimpleFAIRModelVisualization;
